import { AutogenLayoutOrderPayables } from "./autogen/AutogenLayoutOrderPayables";
import { DateRange, DateUtil, ModelRow, ObjectUtil } from "@mcleod/core";
import {
    ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceRemoteDataChangeEvent, Layout,
    ModelDataChangeType, Snackbar, Table, TableRowDisplayEvent, TableSelectionEvent
} from "@mcleod/components";
import { LMEUserSettings } from "@mcleod/general";
import { ModelOrderPayablesProfile, RowOrderPayablesProfile } from "./models/ModelOrderPayablesProfile";
import { OrderPayablesFilter } from "./OrderPayablesFilter";
import { FilterPanel } from "@mcleod/dispatch";
import { OrderPayablesUtil } from "./OrderPayablesUtil";
import { ArchiveVoidedPayables } from "./ArchiveVoidedPayables";
import { OrderPayablesProcess } from "./OrderPayablesProcess";

export interface OrderPayablesTabLayout extends Layout {
    tableOrderPayables: Table;
    getLayoutBaseFilter: (row: ModelRow) => boolean;
    filterPanel: FilterPanel;
    getProfileRow: () => RowOrderPayablesProfile;
}

 class OrderPayableDataByPayee {
    private readonly settlements: Map<string, ModelRow>;
    private readonly adjustments: Map<string, ModelRow>;

    constructor() {
        this.settlements = new Map<string, ModelRow>();
        this.adjustments = new Map<string, ModelRow>();
    }

    addSettlement(row: ModelRow) {
        this.settlements.set(row.get("record_id"), row);
    }

    addAdjustment(row: ModelRow) {
        this.adjustments.set(row.get("record_id"), row);
    }

    getSettlements(): Map<string, ModelRow> {
        return this.settlements;
    }

    getAdjustments(): Map<string, ModelRow> {
        return this.adjustments;
    }

    deteleSettlement(row: ModelRow) {
        this.settlements.delete(row.get("record_id"));
    }

    deleteAdjustment(row: ModelRow) {
        this.adjustments.delete(row.get("record_id"));
    }
 }

class OrderPayableData {
    private settlement: ModelRow;
    private readonly adjustments: Map<string, ModelRow>;

    constructor() {
        this.settlement = null;
        this.adjustments = new Map<string, ModelRow>();
    }

    hasSettlement(): boolean {
        return this.settlement != null;
    }

    getSettlement(): ModelRow {
        return this.settlement;
    }

    getAdjustments(): Map<string, ModelRow> {
        return this.adjustments;
    }

    addSettlement(row: ModelRow) {
        this.settlement = row;
    }

    addAdjustment(row: ModelRow) {
        this.adjustments.set(row.get("record_id"), row);
    }

    deleteSettlement() {
        this.settlement = null;
    }

    deleteAdjustment(row: ModelRow) {
        this.adjustments.delete(row.get("record_id"));
    }
}

export class PayablesLookup {
    private payables: Map<String, OrderPayableData>;
    private payablesByPayee: Map<String, OrderPayableDataByPayee>;

    constructor() {
        this.payables = new Map<String, OrderPayableData>();
        this.payablesByPayee = new Map<String, OrderPayableDataByPayee>();
    }

    addData(row: ModelRow) {
        const entry = this.payables.get(PayablesLookup.key(row));
        if (entry) {
            if (row.getString("record_type") == "S") {
                entry.addSettlement(row);
            } else {
                entry.addAdjustment(row);
            }
        } else {
            const payableData = new OrderPayableData();
            if (row.getString("record_type") == "S") {
                payableData.addSettlement(row);
            } else {
                payableData.addAdjustment(row);
            }
            this.payables.set(PayablesLookup.key(row), payableData);
        }

        const payeeEntry = this.payablesByPayee.get(row.get("payee_id"));
        if (payeeEntry) {
            if (row.getString("record_type") == "S") {
                payeeEntry.addSettlement(row);
            } else {
                payeeEntry.addAdjustment(row);
            }

        } else {
            const payableDataByPayee = new OrderPayableDataByPayee();
            if (row.getString("record_type") == "S") {
                payableDataByPayee.addSettlement(row);
            } else {
                payableDataByPayee.addAdjustment(row);
            }
            this.payablesByPayee.set(row.get("payee_id"), payableDataByPayee);
        }
    }

    deleteData(row: ModelRow) {
        const entry = this.payables.get(PayablesLookup.key(row));

        if (entry) {
            if (row.getString("record_type") == "S") {
                entry.deleteSettlement();
            } else {
                entry.deleteAdjustment(row);
            }
        }

        const payeeEntry = this.payablesByPayee.get(row.get("payee_id"));
        if (payeeEntry) {
            if (row.getString("record_type") == "S") {
                payeeEntry.deteleSettlement(row);
            } else {
                payeeEntry.deleteAdjustment(row);
            }
        }
    }


    getSettlementWithKey(key: string): ModelRow {
        const data = this.payables.get(key);
        if (data == undefined) return null;

        if (data.hasSettlement == null) return null;

        return data.getSettlement();
    }

    getAdjustmentsWithKey(key: string): Map<string, ModelRow> {
        const payables = this.payables.get(key);
        if (payables == undefined) return new Map<string, ModelRow>;
        return payables.getAdjustments();
    }

    getPayablesByPayee(payeeId): OrderPayableDataByPayee {
        const data = this.payablesByPayee.get(payeeId);
        if (data ==undefined) {
            return new OrderPayableDataByPayee();
        }
        return data;
    }

    static key(row: ModelRow): string {
        return `${row.getString("order_id", "")}|${row.getString("movement_id", "")}|${row.getString("payee_id", "")}`;
    }
}

export class OrderPayables extends AutogenLayoutOrderPayables {
    private _profileRow: RowOrderPayablesProfile;
    private profileFilter: OrderPayablesFilter;
    private payablesLookupMap: PayablesLookup;

    static isOrderPayablesLayoutTab(layout: Layout): boolean {
        return "getLayoutBaseFilter" in layout && "tableOrderPayables" in layout;
    }

    onLoad(): void | Promise<void> {
        this.tabsetOrderPayables.forEveryChildComponent((component) => {
            if (component instanceof Layout && OrderPayables.isOrderPayablesLayoutTab(component)) {
                const table = (component as OrderPayablesTabLayout).tableOrderPayables;
                table.addSelectionListener((event: TableSelectionEvent) => {
                    if (event.newRow?.data?.get("record_type") === "S") {
                        table.detailLayout = "lme/settlement/Settlement";
                    } else {
                        table.detailLayout = "lme/settlement/PendingPayAdjustment";
                    }
                });

                table.addRowDisplayListener((event: TableRowDisplayEvent) => {
                    const tableRow = event.getTableRow();
                    OrderPayablesUtil.setPayAdjustmentInfo(tableRow, this.layoutGroupBy, this.payablesLookupMap);
                });

                (component as OrderPayablesTabLayout).getProfileRow = () => this.profileRow;
                if (component instanceof OrderPayablesProcess) {
                    component.getPayablesLookupMap = () => this.payablesLookupMap;
                }
            }
        });
        this.layoutheaderOrderPayables.addRightTools(OrderPayablesUtil.generateAddButton());
    }

    sourceOrderPayablesCacheAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.payablesLookupMap = new PayablesLookup();
            this.profileFilter.payablesLookupMap = this.payablesLookupMap;
            // Build the lookup map fully before filtering so the filter can see every settlement
            // (a pending adjustment may be evaluated before its settlement in the unordered result set).
            const rows = this.sourceOrderPayablesCache.data;
            rows.forEach(row => this.payablesLookupMap.addData(row));
            this.sourceOrderPayablesCache.data = rows.filter(row => this.profileFilter.dataPasses(row));
            this.sourceOrderPayablesCache.displayDataInBoundComponents();
            this.layoutGroupBy.initialize(this.tabsetOrderPayables, this.profileRow.get("group_by_default"), this.profileFilter, this.payablesLookupMap);
        }
    }

    sourceOrderPayablesCacheOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type as ModelDataChangeType;
        const changeData = event.change.data as ModelRow;
        if (!this.profileFilter.dataPasses(changeData)) {
            changeType = ModelDataChangeType.DELETE;
        }
        const oldData = this.sourceOrderPayablesCache.data.find(row => row.get("record_id") == changeData.get("record_id"));
        if (oldData != null && ObjectUtil.deepEqual(oldData.data, changeData.data)) {
            return;
        }
        this.sourceOrderPayablesCache.handleDataChange({
            type: changeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        this.handleChangeInLookupData(oldData, changeData, changeType);
        this.layoutGroupBy.updateGroupByTable(oldData, changeData, changeType);
        this.rollUpPayAdjustments(changeData);
        event.preventDefault();
    }

    async loadProfile(profile?: string | ModelRow) {
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? LMEUserSettings.get()?.order_payables_profile;
        if (profileId == null)
            Snackbar.showSnackbar("A default Order Payables profile is not set. One can be set via User Settings.", { persist: true });
        else {
            const profileRow = await new ModelOrderPayablesProfile().searchSingle({ id: profileId });
            if (profileRow == null) {
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            } else {
                this.setProfileRow(profileRow);
            }
        }
    }

    public get profileRow(): ModelRow {
        return this._profileRow;
    }

    public setProfileRow(value: ModelRow) {
        if (value != this._profileRow) {
            this._profileRow = value;
            this.profileFilter = new OrderPayablesFilter(value);
            if (this._profileRow != null) {
                this.textboxDateRange.text = DateRange.formatWithUnboundDates(this._profileRow.get("start_date"), this._profileRow.get("cutoff_date"));
                this.textboxDateRange.visible = !this.textboxDateRange.isEmpty();
                this.sourceOrderPayablesCache.search({ profile_row: this.profileRow });
            }
        }
    }

    buttonArchiveVoidsOnClick(event: ClickEvent) {
        const cutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"));
        ArchiveVoidedPayables.showSlideout({ cutoff_date: cutoffDate, is_driver_settlement: false });
    }

    sourceVoidedPayablesOrderPayablesAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.updateArchiveVoidsButton();
        }
    }

    private updateArchiveVoidsButton() {
        this.buttonArchiveVoids.caption = `Voids Ready To Archive (${this.sourceVoidedPayablesOrderPayables.data.length})`;
        this.buttonArchiveVoids.enabled = this.sourceVoidedPayablesOrderPayables.data.length > 0;
    }

    sourceVoidedPayablesOrderPayablesOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const changeData = event.change.data;
        if (changeData.isNull("table_name")) return;
        const row = this.sourceVoidedPayablesOrderPayables.data.find(row => row.get("id") == changeData.get("id"));
        if (changeData.get("ready_to_pay_flag") == "V" && row == null) {
            this.sourceVoidedPayablesOrderPayables.data.push(changeData);
        } else if (changeData.get("ready_to_pay_flag") != "V" && row != null) {
            this.sourceVoidedPayablesOrderPayables.data = this.sourceVoidedPayablesOrderPayables.data.filter(row => row.get("id") != changeData.get("id"));
        }
        this.updateArchiveVoidsButton();
        event.preventDefault();
    }

    public rollUpPayAdjustments(newData: ModelRow) {
        if (newData.get("record_type") == "S") {
            const key = PayablesLookup.key(newData);
            const adjustmentsData = this.payablesLookupMap.getAdjustmentsWithKey(key);
            for (const [, modelRow] of adjustmentsData) {
                if (OrderPayablesUtil.matchingPayable(modelRow, newData) && modelRow.get("record_type") == "P") {
                    const index = this.sourceOrderPayablesCache.findRowInData(modelRow);
                    this.sourceOrderPayablesCache.redisplayRow(index, modelRow, ModelDataChangeType.UPDATE);
                }
            }
        } else if (newData.get("record_type") == "P") {
            const key = PayablesLookup.key(newData);
            const modelRow = this.payablesLookupMap.getSettlementWithKey(key);
            if (OrderPayablesUtil.matchingPayable(modelRow, newData) && modelRow.get("record_type") == "S") {
                const index = this.sourceOrderPayablesCache.findRowInData(modelRow);
                this.sourceOrderPayablesCache.redisplayRow(index, modelRow, ModelDataChangeType.UPDATE);
            }
        }
    }

    handleChangeInLookupData(oldData: ModelRow, newData: ModelRow, changeType: ModelDataChangeType) {
        if (changeType == ModelDataChangeType.DELETE) {
            this.payablesLookupMap.deleteData(oldData);
        } else if (changeType == ModelDataChangeType.UPDATE) {
            this.payablesLookupMap.addData(newData);
        }
    }

    override get windowTitle(): string {
        const title = "Order Payables";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }
}
